
import { useRef, useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDispatch } from 'react-redux';
import {
  getEmpresaEventosPublicadosActivos,
  getEmpresaEventosPorPublicar,
  getEmpresaEventosFinalizados,
} from '@/features/empresa/empresaThunks';
import { getMisTicketsComprados, getReferralLinksDeUsuario, getEventosConTicketsDeUsuario } from '@/features/user/userThunks';
import { getMyReferralLinks } from '@/features/embajador/embajadorThunks';
import TicketCardPerfilEmpresaViewOwnEmpresa from '../TicketCardPerfilEmpresaViewOwnEmpresa';
import TicketCardPerfilEmpresa from '../TicketCardPerfilEmpresa';
import TicketCardPerfilUsuario from '../TicketCardPerfilUsuario';
import TicketCardReferralLink from '../TicketCardReferralLink';
import TicketCardCreateNew from '../TicketCardCreateNew';

export default function TicketCarousel({ tickets, TicketItem, sectionType, uid, onLoadMore, onCreateClick, hasMore: externalHasMore, isLoadingMore: externalIsLoading, ...additionalProps }) {
  const scrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const dispatch = useDispatch();

  // Detectar si el TicketItem es TicketCardPerfilEmpresaViewOwnEmpresa, TicketCardPerfilUsuario o TicketCardReferralLink
  const shouldPaginate =
    (TicketItem === TicketCardPerfilEmpresaViewOwnEmpresa ||
     TicketItem === TicketCardPerfilEmpresa ||
     TicketItem === TicketCardPerfilUsuario ||
     TicketItem === TicketCardReferralLink)
    && sectionType
    && uid;

  // Si hay hasMore externo (para eventos públicos), usarlo en lugar del interno
  const showLoadMore = shouldPaginate 
    ? hasMore 
    : (externalHasMore !== undefined ? externalHasMore : false);
  
  const showLoadingIndicator = shouldPaginate 
    ? isLoading 
    : (externalIsLoading !== undefined ? externalIsLoading : false);

  const scroll = (direction) => {
    const container = scrollRef.current;
    if (!container) return;

    const scrollAmount = 500;
    const newScroll = direction === 'left'
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;

    container.scrollTo({ left: newScroll, behavior: 'smooth' });
  };

  const loadMoreEvents = useCallback(async () => {
    if (isLoading || !hasMore || !shouldPaginate) return;

    setIsLoading(true);
    const nextPage = currentPage + 1;

    try {
      let response;
      
      // Llamar al thunk correspondiente según el tipo de sección
      switch (sectionType) {
        case 'eventosActivos':
          response = await dispatch(getEmpresaEventosPublicadosActivos({ 
            uid, 
            page: nextPage, 
            limit: 10 
          })).unwrap();
          break;
        case 'eventosPorPublicar':
          response = await dispatch(getEmpresaEventosPorPublicar({ 
            uid, 
            page: nextPage, 
            limit: 10 
          })).unwrap();
          break;
        case 'eventosFinalizados':
          response = await dispatch(getEmpresaEventosFinalizados({ 
            uid, 
            page: nextPage, 
            limit: 10 
          })).unwrap();
          break;
        case 'ticketsProximos':
          const now = new Date().toISOString();
          response = await dispatch(getMisTicketsComprados({ 
            vigente: true,
            eventoFechaHoraInicioDesde: now,
            sortBy: 'eventoFechaHoraInicio',
            sortOrder: 'asc',
            page: nextPage, 
            limit: 10 
          })).unwrap();
          break;
        case 'ticketsPasados':
          const currentDate = new Date().toISOString();
          response = await dispatch(getMisTicketsComprados({ 
            eventoFechaHoraFinHasta: currentDate,
            sortBy: 'eventoFechaHoraFin',
            sortOrder: 'desc',
            page: nextPage, 
            limit: 10 
          })).unwrap();
          break;
        case 'referralLinksActivos':
          const nowActivos = new Date().toISOString();
          response = await dispatch(getReferralLinksDeUsuario({ 
            uid,
            filters: {
              eventoEstado: 'activo',
              eventoFechaHoraInicioDesde: nowActivos,
              sortBy: 'eventoFechaHoraInicio',
              sortOrder: 'asc',
              page: nextPage, 
              limit: 10
            }
          })).unwrap();
          break;
        case 'referralLinksFinalizados':
          const nowFinalizados = new Date().toISOString();
          response = await dispatch(getReferralLinksDeUsuario({ 
            uid,
            filters: {
              eventoEstado: 'activo',
              eventoFechaHoraFinHasta: nowFinalizados,
              sortBy: 'eventoFechaHoraFin',
              sortOrder: 'desc',
              page: nextPage, 
              limit: 10
            }
          })).unwrap();
          break;
        case 'referralLinksCancelados':
          response = await dispatch(getReferralLinksDeUsuario({ 
            uid,
            filters: {
              eventoEstado: 'cancelado',
              sortBy: 'eventoFechaHoraInicio',
              sortOrder: 'desc',
              page: nextPage, 
              limit: 10
            }
          })).unwrap();
          break;
        case 'eventosPasados':
          const nowPasados = new Date().toISOString();
          response = await dispatch(getEventosConTicketsDeUsuario({
            uid,
            filters: {
              eventoFechaHoraFinHasta: nowPasados,
              sortBy: 'eventoFechaHoraInicio',
              sortOrder: 'desc',
              page: nextPage,
              limit: 10
            }
          })).unwrap();
          break;
        default:
          return;
      }

      const newEventos = response.eventos || response.tickets || response.links || [];
      
      console.log('🔵 Eventos/Tickets recibidos:', newEventos.length);
      console.log('🔵 Página actual:', currentPage, '→ Próxima:', nextPage);
      console.log('🔵 hasNextPage:', response.pagination?.hasNextPage);
      
      // IMPORTANTE: Actualizar currentPage ANTES de llamar a onLoadMore
      setCurrentPage(nextPage);
      
      // Llamar a onLoadMore con los nuevos eventos
      if (onLoadMore && newEventos.length > 0) {
        onLoadMore(newEventos);
      }
      
      // Verificar si hay más páginas
      if (newEventos.length === 0 || !response.pagination?.hasNextPage) {
        setHasMore(false);
      }
    } catch (error) {
      console.error('❌ Error al cargar más eventos:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, hasMore, shouldPaginate, currentPage, sectionType, uid, dispatch, onLoadMore]);

  const checkScroll = useCallback(() => {
    const container = scrollRef.current;
    if (!container) return;

    setShowLeft(container.scrollLeft > 0);
    setShowRight(container.scrollLeft + container.offsetWidth < container.scrollWidth);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;
    
    container.addEventListener('scroll', checkScroll);
    checkScroll();
    
    return () => container.removeEventListener('scroll', checkScroll);
  }, [checkScroll, tickets]);

  // Debugging: Mostrar estado actualizado del componente
  useEffect(() => {
    console.log('🟢 TicketCarousel actualizado:', {
      ticketsLength: tickets.length,
      currentPage,
      hasMore,
      isLoading,
      sectionType
    });
  }, [tickets, currentPage, hasMore, isLoading, sectionType]);

  return (
    <div className="relative w-full mb-6">
      {/* Botón Izquierdo */}
      {showLeft && (
        <button
          onClick={() => scroll('left')}
          className="absolute left-0 top-1/2 h-full transform -translate-y-1/2 z-10 bg-white/20 hover:bg-aw-bluedark-75 transition duration-300 p-2 rounded-tr-3xl rounded-br-3xl"
        >
          <FontAwesomeIcon icon="fa-solid fa-chevron-left" className="text-white text-lg" />
        </button>
      )}

      {/* Contenedor Scroll */}
      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth scrollbar-box pb-4 w-96 md:w-full"
        style={{ scrollBehavior: 'smooth' }}
      >
        {/* Card especial para crear evento (solo en eventosPorPublicar) */}
        {sectionType === 'eventosPorPublicar' && onCreateClick && (
          <TicketCardCreateNew onCreateClick={onCreateClick} />
        )}
        
        {tickets.map(ticket => {
          // Si es TicketCardReferralLink, usar la prop "referralLink"
          if (TicketItem === TicketCardReferralLink) {
            return <TicketItem key={`ticket-${ticket.uid}`} referralLink={ticket} {...additionalProps} />;
          }
          // Para otros componentes, usar la prop "ticket"
          return <TicketItem key={`ticket-${ticket.uid}`} ticket={ticket} {...additionalProps} />;
        })}
        
        {/* Botón para cargar más eventos - Soporta paginación interna y externa */}
        {showLoadMore && !showLoadingIndicator && (
          <div className="min-w-[200px] max-w-[200px] flex items-center justify-center h-fit">
            <button
              onClick={shouldPaginate ? loadMoreEvents : onLoadMore}
              className="w-full h-52 flex flex-col items-center justify-center gap-4 bg-aw-bluedark-50 hover:bg-aw-bluedark-75 border-2 border-dashed border-aw-green hover:border-solid rounded-xl transition-all duration-300 hover:scale-95"
            >
              <FontAwesomeIcon icon="fa-solid fa-plus-circle" className="text-aw-green text-4xl" />
              <span className="text-white font-semibold text-sm">Mostrar más</span>
            </button>
          </div>
        )}
        
        {/* Indicador de carga - Soporta loading interno y externo */}
        {showLoadingIndicator && (
          <div className="min-w-[200px] max-w-[200px] flex items-center justify-center">
            <div className="text-white">
              <FontAwesomeIcon icon="fa-solid fa-spinner" className="animate-spin text-2xl" />
            </div>
          </div>
        )}
      </div>

      {/* Botón Derecho */}
      {showRight && (
        <button
          onClick={() => scroll('right')}
          className="absolute right-0 top-1/2 h-full transform -translate-y-1/2 z-10 bg-white/20 hover:bg-aw-bluedark-75 transition duration-300 p-2 rounded-tl-3xl rounded-bl-3xl"
        >
          <FontAwesomeIcon icon="fa-solid fa-chevron-right" className="text-white text-lg" />
        </button>
      )}
    </div>
  );
}
